import { useState, useEffect } from 'react';
import { Users, Calendar, TrendingUp, AlertCircle } from '../../icons/wp-icons';
import { getHeuteAbwesenheiten, getWocheAbwesenheiten, getStatistik } from '../../api/abwesenheitApi';
import { useAuth } from '../../context/AuthContext';

const TYP_LABELS = {
  URLAUB: { label: 'Urlaub', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  KRANKHEIT: { label: 'Krank', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  FORTBILDUNG: { label: 'Fortbildung', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  HOMEOFFICE: { label: 'Homeoffice', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  SONSTIGES: { label: 'Sonstiges', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' }
};

function AbwesenheitUebersicht() {
  const { user } = useAuth();
  const isAdmin = user?.rolle === 'admin';

  const [heuteAbwesenheiten, setHeuteAbwesenheiten] = useState([]);
  const [wocheAbwesenheiten, setWocheAbwesenheiten] = useState([]);
  const [statistik, setStatistik] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [heute, woche, stats] = await Promise.all([
        getHeuteAbwesenheiten(),
        getWocheAbwesenheiten(),
        getStatistik()
      ]);
      setHeuteAbwesenheiten(heute);
      setWocheAbwesenheiten(woche);
      setStatistik(stats);
    } catch (err) {
      console.error('Fehler:', err);
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Wochentage für Übersicht
  const getWochentage = () => {
    const tage = [];
    const heute = new Date();
    const montag = new Date(heute);
    montag.setDate(heute.getDate() - heute.getDay() + 1);

    for (let i = 0; i < 7; i++) {
      const tag = new Date(montag);
      tag.setDate(montag.getDate() + i);
      tage.push(tag);
    }
    return tage;
  };

  // Prüfen ob Abwesenheit an einem Tag ist
  const istAbwesendAmTag = (abwesenheit, tag) => {
    const start = new Date(abwesenheit.startDatum);
    const ende = new Date(abwesenheit.endDatum);
    start.setHours(0, 0, 0, 0);
    ende.setHours(23, 59, 59, 999);
    tag.setHours(12, 0, 0, 0);
    return tag >= start && tag <= ende;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        {error}
      </div>
    );
  }

  const wochentage = getWochentage();
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-6">
      {/* Heute abwesend */}
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-light rounded-lg">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Heute abwesend</h2>
          <span className="ml-auto px-2 py-1 bg-gray-100 rounded-full text-sm font-medium">
            {heuteAbwesenheiten.length}
          </span>
        </div>

        {heuteAbwesenheiten.length === 0 ? (
          <p className="text-text-muted text-center py-4">
            Heute sind alle Mitarbeiter anwesend
          </p>
        ) : (
          <div className="space-y-2">
            {heuteAbwesenheiten.map(a => {
              const typInfo = TYP_LABELS[a.typ] || TYP_LABELS.SONSTIGES;
              return (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${typInfo.dot}`}></div>
                    <span className="font-medium">{a.benutzer.name}</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${typInfo.color}`}>
                    {typInfo.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Wochenübersicht */}
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold">Diese Woche</h2>
        </div>

        {wocheAbwesenheiten.length === 0 ? (
          <p className="text-text-muted text-center py-4">
            Keine Abwesenheiten diese Woche
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-medium text-text-muted">Mitarbeiter</th>
                  {wochentage.map((tag, i) => {
                    const isHeute = tag.toDateString() === heute.toDateString();
                    const isWochenende = i >= 5;
                    return (
                      <th
                        key={i}
                        className={`text-center py-2 px-2 font-medium text-sm ${
                          isHeute ? 'bg-primary-light text-primary rounded' : isWochenende ? 'text-gray-400' : 'text-text-muted'
                        }`}
                      >
                        <div>{['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][i]}</div>
                        <div className="text-xs">{formatDate(tag)}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Gruppiere nach Benutzer */}
                {[...new Map(wocheAbwesenheiten.map(a => [a.benutzerId, a.benutzer])).values()].map(benutzer => {
                  const benutzerAbwesenheiten = wocheAbwesenheiten.filter(a => a.benutzerId === benutzer.id);
                  return (
                    <tr key={benutzer.id} className="border-b border-border last:border-0">
                      <td className="py-3 px-3 font-medium">{benutzer.name}</td>
                      {wochentage.map((tag, i) => {
                        const abwesenheit = benutzerAbwesenheiten.find(a => istAbwesendAmTag(a, new Date(tag)));
                        const typInfo = abwesenheit ? (TYP_LABELS[abwesenheit.typ] || TYP_LABELS.SONSTIGES) : null;
                        return (
                          <td key={i} className="text-center py-3 px-2">
                            {abwesenheit ? (
                              <span
                                className={`inline-block w-6 h-6 rounded-full ${typInfo.dot}`}
                                title={`${typInfo.label}${abwesenheit.grund ? `: ${abwesenheit.grund}` : ''}`}
                              />
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Legende */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-border">
          {Object.entries(TYP_LABELS).map(([key, info]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <span className={`w-3 h-3 rounded-full ${info.dot}`}></span>
              <span className="text-text-muted">{info.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Urlaubstage-Statistik (nur Admin) */}
      {isAdmin && statistik.length > 0 && (
        <div className="bg-surface rounded-card shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold">Urlaubstage {new Date().getFullYear()}</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-text-muted">Mitarbeiter</th>
                  <th className="text-center py-3 px-4 font-medium text-text-muted">Gesamt</th>
                  <th className="text-center py-3 px-4 font-medium text-text-muted">Genommen</th>
                  <th className="text-center py-3 px-4 font-medium text-text-muted">Beantragt</th>
                  <th className="text-center py-3 px-4 font-medium text-text-muted">Verfügbar</th>
                  <th className="text-center py-3 px-4 font-medium text-text-muted">Krankheit</th>
                </tr>
              </thead>
              <tbody>
                {statistik.map(stat => (
                  <tr key={stat.benutzerId} className="border-b border-border last:border-0 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{stat.benutzerName}</td>
                    <td className="py-3 px-4 text-center">{stat.urlaubstageGesamt}</td>
                    <td className="py-3 px-4 text-center text-green-600">{stat.urlaubstageVerbraucht}</td>
                    <td className="py-3 px-4 text-center text-orange-500">{stat.urlaubstageBeantragt}</td>
                    <td className="py-3 px-4 text-center font-semibold">{stat.urlaubstageVerfuegbar}</td>
                    <td className="py-3 px-4 text-center text-red-500">{stat.krankheitstage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AbwesenheitUebersicht;
